import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  InputAdornment,
  Avatar,
  Stack,
  Divider,
} from '@mui/material';
import {
  Article as ArticleIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as RevisionIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  PushPin as PinIcon,
  Archive as ArchiveIcon,
  AutoAwesome as EditorPickIcon,
  Visibility as ViewIcon,
  Info as InfoIcon,
  TravelExplore as SeoIcon,
  FactCheck as VerificationIcon,
  BarChart as StatsIcon,
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import { useSnackbar } from 'notistack';
import * as articleService from '../services/articleService';
import { getCategories, ArticleCategory } from '../services/articleCategoryService';
import MyShiftStatusBanner from '../components/common/MyShiftStatusBanner';
import {
  ResponsiveTable,
  ResponsiveModal,
  ConfirmDialog,
  DetailSection,
} from '../components/common';
import type { Column } from '../components/common';
import AssigneeCell from '../components/common/AssigneeCell';
import BlogHeroLayout from '../components/articles/BlogHeroLayout';

// ==================== MAIN COMPONENT ====================

const ArticleManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Data state
  const [articles, setArticles] = useState<articleService.Article[]>([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState<ArticleCategory[]>([]);

  // Action dialogs
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<articleService.Article | null>(null);
  const [approvalNote, setApprovalNote] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [revisionNote, setRevisionNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Blog hero layout card — bump to refetch after a table-side position change
  const [heroLayoutKey, setHeroLayoutKey] = useState(0);

  // Detail view
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailArticle, setDetailArticle] = useState<articleService.Article | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Map tab index to status filter
  const getStatusForTab = (tab: number): string => {
    switch (tab) {
      case 0: return 'pending_review';
      case 1: return 'published';
      case 2: return 'rejected';
      case 3: return 'archived';
      default: return 'pending_review';
    }
  };

  // Load categories for filter dropdown
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getCategories();
        setCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('خطا در بارگذاری دسته‌بندی‌ها:', err);
      }
    };
    loadCategories();
  }, []);

  // Load articles
  const loadArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = getStatusForTab(activeTab);
      // For the "rejected" tab, we fetch drafts that have a rejectionReason
      const fetchStatus = activeTab === 2 ? 'draft' : status;

      const response = await articleService.getArticles(page + 1, rowsPerPage, {
        status: fetchStatus,
        category: categoryFilter || undefined,
        search: searchQuery || undefined,
      });

      let docs = response.docs || [];

      // For rejected tab, filter to only drafts with rejection reason
      if (activeTab === 2) {
        docs = docs.filter((a) => a.verificationInfo?.rejectionReason);
      }

      setArticles(docs);
      setTotalDocs(activeTab === 2 ? docs.length : (response.totalDocs || 0));
    } catch (err: any) {
      setError(err.message || 'خطا در بارگذاری مقالات');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, rowsPerPage, searchQuery, categoryFilter]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  // Tab change resets pagination
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPage(0);
  };

  // ==================== ACTION HANDLERS ====================

  const handleViewClick = async (article: articleService.Article) => {
    setDetailArticle(article);
    setDetailDialogOpen(true);
    setDetailLoading(true);
    try {
      // لیست ادمین content/tags را populate نمی‌کند؛ جزئیات کامل جداگانه دریافت می‌شود
      const full = await articleService.getArticle(article._id);
      setDetailArticle(full);
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در دریافت جزئیات مقاله', { variant: 'error' });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApproveClick = (article: articleService.Article) => {
    setSelectedArticle(article);
    setApprovalNote('');
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!selectedArticle) return;
    setSubmitting(true);
    try {
      await articleService.approveArticle(selectedArticle._id, approvalNote || undefined);
      enqueueSnackbar('مقاله با موفقیت تایید شد', { variant: 'success' });
      setApproveDialogOpen(false);
      loadArticles();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در تایید مقاله', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectClick = (article: articleService.Article) => {
    setSelectedArticle(article);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedArticle || !rejectionReason.trim()) {
      enqueueSnackbar('لطفا دلیل رد را وارد کنید', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      await articleService.rejectArticle(selectedArticle._id, rejectionReason);
      enqueueSnackbar('مقاله رد شد', { variant: 'success' });
      setRejectDialogOpen(false);
      loadArticles();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در رد مقاله', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevisionClick = (article: articleService.Article) => {
    setSelectedArticle(article);
    setRevisionNote('');
    setRevisionDialogOpen(true);
  };

  const handleRevisionConfirm = async () => {
    if (!selectedArticle || !revisionNote.trim()) {
      enqueueSnackbar('لطفا یادداشت بازنگری را وارد کنید', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      await articleService.requestRevision(selectedArticle._id, revisionNote);
      enqueueSnackbar('درخواست بازنگری ارسال شد', { variant: 'success' });
      setRevisionDialogOpen(false);
      loadArticles();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در ارسال درخواست بازنگری', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (article: articleService.Article) => {
    setSelectedArticle(article);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedArticle) return;
    setSubmitting(true);
    try {
      await articleService.deleteArticle(selectedArticle._id);
      enqueueSnackbar('مقاله با موفقیت حذف شد', { variant: 'success' });
      setDeleteDialogOpen(false);
      loadArticles();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در حذف مقاله', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleFeatured = async (article: articleService.Article) => {
    try {
      await articleService.toggleFeatured(article._id);
      enqueueSnackbar(
        article.isFeatured ? 'مقاله از ویژه‌ها حذف شد' : 'مقاله به ویژه‌ها اضافه شد',
        { variant: 'success' }
      );
      loadArticles();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در تغییر وضعیت', { variant: 'error' });
    }
  };

  const handleTogglePinned = async (article: articleService.Article) => {
    try {
      await articleService.togglePinned(article._id);
      enqueueSnackbar(
        article.isPinned ? 'سنجاق مقاله برداشته شد' : 'مقاله سنجاق شد',
        { variant: 'success' }
      );
      loadArticles();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در تغییر وضعیت', { variant: 'error' });
    }
  };

  const handleToggleEditorPick = async (article: articleService.Article) => {
    try {
      await articleService.toggleEditorPick(article._id);
      enqueueSnackbar(
        article.isEditorPick ? 'از برگزیده سردبیر حذف شد' : 'به برگزیده سردبیر اضافه شد',
        { variant: 'success' }
      );
      loadArticles();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در تغییر وضعیت', { variant: 'error' });
    }
  };

  const handleHeroPositionChange = async (article: articleService.Article, position: string | null) => {
    try {
      await articleService.setBlogHeroPosition(article._id, position);
      enqueueSnackbar(
        position
          ? `مقاله در جایگاه «${articleService.BLOG_HERO_POSITION_LABELS[position as articleService.BlogHeroPosition]}» قرار گرفت`
          : 'مقاله از جایگاه هیرو حذف شد',
        { variant: 'success' }
      );
      setHeroLayoutKey((k) => k + 1);
      loadArticles();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در تنظیم جایگاه هیرو', { variant: 'error' });
    }
  };

  const handleArchive = async (article: articleService.Article) => {
    try {
      await articleService.archiveArticle(article._id);
      enqueueSnackbar('مقاله آرشیو شد', { variant: 'success' });
      loadArticles();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در آرشیو مقاله', { variant: 'error' });
    }
  };

  // ==================== FORMAT HELPERS ====================

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fa-IR');
  };

  const formatDateTime = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('fa-IR');
  };

  const formatNumber = (num?: number) => {
    return (num || 0).toLocaleString('fa-IR');
  };

  // ==================== TABLE COLUMNS ====================

  const columns: Column<articleService.Article>[] = [
    {
      id: 'title',
      label: 'عنوان',
      priority: 1,
      minWidth: 240,
      format: (_v, article) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {article.featuredImage && (
            <Avatar
              variant="rounded"
              src={article.featuredImage}
              sx={{ width: 48, height: 48 }}
            />
          )}
          <Box>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 220 }}>
              {article.title}
            </Typography>
            {!!article.readingTime && (
              <Typography variant="caption" color="text.secondary">
                {formatNumber(article.readingTime)} دقیقه مطالعه
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      id: 'author',
      label: 'نویسنده',
      priority: 2,
      format: (_v, article) => (
        <>
          <Typography variant="body2">{article.author?.name || 'نامشخص'}</Typography>
          {article.author?.email && (
            <Typography variant="caption" color="text.secondary">
              {article.author.email}
            </Typography>
          )}
        </>
      ),
    },
    {
      id: 'category',
      label: 'دسته‌بندی',
      priority: 3,
      format: (_v, article) =>
        article.categoryId?.name ? (
          <Chip label={article.categoryId.name} size="small" variant="outlined" />
        ) : (
          <Typography variant="caption" color="text.secondary">بدون دسته‌بندی</Typography>
        ),
    },
    {
      id: 'createdAt',
      label: 'تاریخ',
      priority: 4,
      format: (_v, article) => (
        <Typography variant="body2">{formatDate(article.createdAt)}</Typography>
      ),
    },
    {
      id: 'status',
      label: 'وضعیت',
      priority: 2,
      format: (_v, article) => (
        <Chip
          label={articleService.getStatusLabel(article.status)}
          color={articleService.getStatusColor(article.status)}
          size="small"
        />
      ),
    },
    {
      id: 'assignedTo',
      label: 'مسئول',
      priority: 3,
      format: (_v, article) => <AssigneeCell value={(article as any).assignedTo} />,
    },
    {
      id: 'viewCount',
      label: 'بازدید',
      align: 'center',
      priority: 5,
      format: (_v, article) => (
        <Typography variant="body2">{formatNumber(article.viewCount)}</Typography>
      ),
    },
    // Rejected tab: show the rejection reason inline
    ...(activeTab === 2
      ? ([
          {
            id: 'rejectionReason',
            label: 'دلیل رد',
            priority: 3,
            format: (_v: any, article: articleService.Article) => (
              <Typography variant="body2" color="error.main" sx={{ maxWidth: 260 }} noWrap>
                {article.verificationInfo?.rejectionReason || '-'}
              </Typography>
            ),
          },
        ] as Column<articleService.Article>[])
      : []),
    // Feature toggles only for published tab
    ...(activeTab === 1
      ? ([
          {
            id: 'features',
            label: 'ویژگی‌ها',
            align: 'center',
            priority: 6,
            format: (_v: any, article: articleService.Article) => (
              <Stack
                direction="row"
                spacing={0}
                justifyContent="center"
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip title={article.isFeatured ? 'حذف از ویژه' : 'افزودن به ویژه'}>
                  <IconButton
                    size="small"
                    color={article.isFeatured ? 'warning' : 'default'}
                    onClick={() => handleToggleFeatured(article)}
                  >
                    {article.isFeatured ? <StarIcon /> : <StarBorderIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={article.isPinned ? 'برداشتن سنجاق' : 'سنجاق کردن'}>
                  <IconButton
                    size="small"
                    color={article.isPinned ? 'primary' : 'default'}
                    onClick={() => handleTogglePinned(article)}
                  >
                    <PinIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={article.isEditorPick ? 'حذف از برگزیده' : 'برگزیده سردبیر'}>
                  <IconButton
                    size="small"
                    color={article.isEditorPick ? 'secondary' : 'default'}
                    onClick={() => handleToggleEditorPick(article)}
                  >
                    <EditorPickIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            ),
          },
          {
            id: 'blogHeroPosition',
            label: 'جایگاه هیرو',
            align: 'center',
            priority: 7,
            format: (_v: any, article: articleService.Article) => (
              <FormControl
                size="small"
                sx={{ minWidth: 130 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Select
                  value={article.blogHeroPosition || ''}
                  onChange={(e) => handleHeroPositionChange(article, e.target.value || null)}
                  displayEmpty
                  sx={{ fontSize: '0.8rem' }}
                >
                  <MenuItem value="">
                    <Typography variant="body2" color="text.secondary">بدون جایگاه</Typography>
                  </MenuItem>
                  {articleService.BLOG_HERO_POSITIONS.map((position) => (
                    <MenuItem key={position} value={position}>
                      {articleService.BLOG_HERO_POSITION_LABELS[position]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ),
          },
        ] as Column<articleService.Article>[])
      : []),
  ];

  // ==================== ROW ACTIONS ====================

  const renderActions = (article: articleService.Article) => (
    <>
      {/* View details — all tabs */}
      <Tooltip title="مشاهده جزئیات">
        <IconButton
          color="info"
          size="small"
          onClick={() => handleViewClick(article)}
        >
          <ViewIcon />
        </IconButton>
      </Tooltip>

      {/* Pending review actions */}
      {activeTab === 0 && (
        <>
          <Tooltip title="تایید">
            <IconButton
              color="success"
              size="small"
              onClick={() => handleApproveClick(article)}
            >
              <ApproveIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="رد">
            <IconButton
              color="error"
              size="small"
              onClick={() => handleRejectClick(article)}
            >
              <RejectIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="درخواست بازنگری">
            <IconButton
              color="warning"
              size="small"
              onClick={() => handleRevisionClick(article)}
            >
              <RevisionIcon />
            </IconButton>
          </Tooltip>
        </>
      )}

      {/* Published actions */}
      {activeTab === 1 && (
        <Tooltip title="آرشیو">
          <IconButton
            color="default"
            size="small"
            onClick={() => handleArchive(article)}
          >
            <ArchiveIcon />
          </IconButton>
        </Tooltip>
      )}

      {/* Common delete action */}
      <Tooltip title="حذف">
        <IconButton
          color="error"
          size="small"
          onClick={() => handleDeleteClick(article)}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    </>
  );

  // ==================== RENDER ====================

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <MyShiftStatusBanner category="article_moderation" />
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <ArticleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          مدیریت مقالات
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={loadArticles} disabled={loading}>
          بروزرسانی
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="جستجو عنوان مقاله..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>دسته‌بندی</InputLabel>
            <Select
              value={categoryFilter}
              label="دسته‌بندی"
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">همه دسته‌بندی‌ها</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat._id} value={cat._id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* چیدمان هیروی بلاگ — فقط روی تب «منتشرشده» چون جایگاه فقط به مقالهٔ منتشرشده می‌خورد */}
      {activeTab === 1 && <BlogHeroLayout key={heroLayoutKey} onChanged={loadArticles} />}

      {/* Tabs */}
      <Paper sx={{ p: 0 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="در انتظار بررسی" />
          <Tab label="منتشرشده" />
          <Tab label="رد شده" />
          <Tab label="آرشیو" />
        </Tabs>

        <Box sx={{ p: { xs: 2, sm: 0 } }}>
          <ResponsiveTable<articleService.Article>
            columns={columns}
            data={articles}
            keyField="_id"
            loading={loading}
            emptyMessage="مقاله‌ای یافت نشد"
            onRowClick={handleViewClick}
            renderActions={renderActions}
            pagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalDocs}
            onPageChange={(p) => setPage(p)}
            onRowsPerPageChange={(rpp) => {
              setRowsPerPage(rpp);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </Box>
      </Paper>

      {/* ==================== DETAIL DIALOG ==================== */}
      <ResponsiveModal
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        title="جزئیات مقاله"
        maxWidth="md"
        icon={<ViewIcon color="info" />}
        actions={
          <>
            <Button onClick={() => setDetailDialogOpen(false)} color="inherit">
              بستن
            </Button>
            {detailArticle?.status === 'pending_review' && (
              <>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<RevisionIcon />}
                  onClick={() => {
                    setDetailDialogOpen(false);
                    handleRevisionClick(detailArticle);
                  }}
                >
                  بازنگری
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<RejectIcon />}
                  onClick={() => {
                    setDetailDialogOpen(false);
                    handleRejectClick(detailArticle);
                  }}
                >
                  رد
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  onClick={() => {
                    setDetailDialogOpen(false);
                    handleApproveClick(detailArticle);
                  }}
                >
                  تایید
                </Button>
              </>
            )}
          </>
        }
      >
        {detailLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!detailLoading && detailArticle && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Cover image */}
            {detailArticle.featuredImage && (
              <Box
                component="img"
                src={detailArticle.featuredImage}
                alt={detailArticle.title}
                sx={{
                  width: '100%',
                  maxHeight: 280,
                  objectFit: 'cover',
                  borderRadius: 2,
                }}
              />
            )}

            {/* Title + status */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6" fontWeight={700}>
                  {detailArticle.title}
                </Typography>
                <Chip
                  label={articleService.getStatusLabel(detailArticle.status)}
                  color={articleService.getStatusColor(detailArticle.status)}
                  size="small"
                />
                {detailArticle.isFeatured && <Chip label="ویژه" color="warning" size="small" variant="outlined" />}
                {detailArticle.isPinned && <Chip label="سنجاق‌شده" color="primary" size="small" variant="outlined" />}
                {detailArticle.isEditorPick && <Chip label="برگزیده سردبیر" color="secondary" size="small" variant="outlined" />}
                {detailArticle.editPending && <Chip label="ویرایش در انتظار" color="info" size="small" variant="outlined" />}
              </Box>
              {detailArticle.summary && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {detailArticle.summary}
                </Typography>
              )}
            </Box>

            {/* Author */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar src={detailArticle.author?.profileImage} sx={{ width: 40, height: 40 }}>
                {detailArticle.author?.name?.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {detailArticle.author?.name || 'نامشخص'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  نویسنده
                </Typography>
              </Box>
            </Box>

            {/* Tags */}
            {!!detailArticle.tags?.length && (
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {detailArticle.tags.map((tag) => (
                  <Chip key={tag._id} label={tag.name} size="small" variant="outlined" />
                ))}
              </Box>
            )}

            {/* Stats */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip icon={<StatsIcon />} label={`بازدید: ${formatNumber(detailArticle.viewCount)}`} size="small" />
              <Chip label={`لایک: ${formatNumber(detailArticle.likeCount)}`} size="small" />
              <Chip label={`دیدگاه: ${formatNumber(detailArticle.commentCount)}`} size="small" />
              <Chip label={`نشان‌شده: ${formatNumber(detailArticle.bookmarkCount)}`} size="small" />
              {!!detailArticle.reportCount && (
                <Chip label={`گزارش: ${formatNumber(detailArticle.reportCount)}`} size="small" color="error" variant="outlined" />
              )}
            </Stack>

            {/* General info */}
            <DetailSection
              title="اطلاعات کلی"
              icon={<InfoIcon fontSize="small" />}
              defaultExpanded
              rows={[
                {
                  label: 'دسته‌بندی',
                  value: detailArticle.categoryId?.name || 'بدون دسته‌بندی',
                },
                {
                  label: 'زمان مطالعه',
                  value: detailArticle.readingTime
                    ? `${formatNumber(detailArticle.readingTime)} دقیقه`
                    : '-',
                },
                { label: 'تاریخ ایجاد', value: formatDateTime(detailArticle.createdAt) },
                {
                  label: 'تاریخ انتشار',
                  value: formatDateTime(detailArticle.publishedAt),
                  hidden: !detailArticle.publishedAt,
                },
                {
                  label: 'انتشار زمان‌بندی‌شده',
                  value: formatDateTime(detailArticle.scheduledAt),
                  hidden: !detailArticle.scheduledAt,
                },
                {
                  label: 'آخرین ویرایش',
                  value: formatDateTime(detailArticle.lastEditedAt),
                  hidden: !detailArticle.lastEditedAt,
                },
                {
                  label: 'شناسه (Slug)',
                  value: detailArticle.slug,
                  copyable: true,
                  mono: true,
                },
                {
                  label: 'کد کوتاه',
                  value: detailArticle.shortCode,
                  copyable: true,
                  mono: true,
                  hidden: !detailArticle.shortCode,
                },
                {
                  label: 'شناسه مقاله',
                  value: detailArticle._id,
                  copyable: true,
                  mono: true,
                },
              ]}
            />

            {/* SEO */}
            <DetailSection
              title="سئو (SEO)"
              icon={<SeoIcon fontSize="small" />}
              rows={[
                { label: 'متا تایتل', value: detailArticle.metaTitle || '-' },
                {
                  label: 'متا دیسکریپشن',
                  value: detailArticle.metaDescription || '-',
                  fullWidth: true,
                },
                {
                  label: 'کلمات کلیدی',
                  value: detailArticle.metaKeywords?.length
                    ? detailArticle.metaKeywords.join('، ')
                    : '-',
                  fullWidth: true,
                },
                {
                  label: 'آدرس Canonical',
                  value: detailArticle.canonicalUrl,
                  copyable: true,
                  mono: true,
                  hidden: !detailArticle.canonicalUrl,
                },
              ]}
            />

            {/* Verification history */}
            {(detailArticle.verificationInfo?.approvalNote ||
              detailArticle.verificationInfo?.rejectionReason ||
              detailArticle.verificationInfo?.revisionNote) && (
              <DetailSection
                title="سوابق بررسی"
                icon={<VerificationIcon fontSize="small" />}
                defaultExpanded
                rows={[
                  {
                    label: 'یادداشت تایید',
                    value: detailArticle.verificationInfo?.approvalNote,
                    fullWidth: true,
                    hidden: !detailArticle.verificationInfo?.approvalNote,
                  },
                  {
                    label: 'تاریخ تایید',
                    value: formatDateTime(detailArticle.verificationInfo?.approvedAt),
                    hidden: !detailArticle.verificationInfo?.approvedAt,
                  },
                  {
                    label: 'دلیل رد',
                    value: detailArticle.verificationInfo?.rejectionReason,
                    fullWidth: true,
                    hidden: !detailArticle.verificationInfo?.rejectionReason,
                  },
                  {
                    label: 'تاریخ رد',
                    value: formatDateTime(detailArticle.verificationInfo?.rejectedAt),
                    hidden: !detailArticle.verificationInfo?.rejectedAt,
                  },
                  {
                    label: 'یادداشت بازنگری',
                    value: detailArticle.verificationInfo?.revisionNote,
                    fullWidth: true,
                    hidden: !detailArticle.verificationInfo?.revisionNote,
                  },
                ]}
              />
            )}

            {/* Article body */}
            <Divider />
            <Typography variant="subtitle1" fontWeight={700}>
              متن مقاله
            </Typography>
            {detailArticle.contentHtml ? (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  maxHeight: 480,
                  overflowY: 'auto',
                  '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                  '& iframe': { maxWidth: '100%' },
                  '& p': { lineHeight: 1.9 },
                }}
                // محتوای HTML مقاله با DOMPurify پاکسازی و امن رندر می‌شود
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(detailArticle.contentHtml),
                }}
              />
            ) : (
              <Alert severity="info">متن رندرشده‌ای برای این مقاله موجود نیست.</Alert>
            )}
          </Box>
        )}
      </ResponsiveModal>

      {/* ==================== APPROVE DIALOG ==================== */}
      <ResponsiveModal
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        title="تایید مقاله"
        maxWidth="sm"
        disableClose={submitting}
        icon={<ApproveIcon color="success" />}
        actions={
          <>
            <Button onClick={() => setApproveDialogOpen(false)} color="inherit">انصراف</Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleApproveConfirm}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} /> : <ApproveIcon />}
            >
              تایید و انتشار
            </Button>
          </>
        }
      >
        {selectedArticle && (
          <Box sx={{ mb: 2 }}>
            <Typography color="text.secondary" gutterBottom>
              عنوان مقاله:
            </Typography>
            <Typography fontWeight={600}>{selectedArticle.title}</Typography>
          </Box>
        )}
        <TextField
          fullWidth
          label="یادداشت تایید (اختیاری)"
          value={approvalNote}
          onChange={(e) => setApprovalNote(e.target.value)}
          multiline
          rows={3}
          sx={{ mt: 1 }}
          placeholder="در صورت تمایل یادداشتی برای نویسنده بنویسید..."
        />
      </ResponsiveModal>

      {/* ==================== REJECT DIALOG ==================== */}
      <ResponsiveModal
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        title="رد مقاله"
        maxWidth="sm"
        disableClose={submitting}
        icon={<RejectIcon color="error" />}
        actions={
          <>
            <Button onClick={() => setRejectDialogOpen(false)} color="inherit">انصراف</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleRejectConfirm}
              disabled={submitting || !rejectionReason.trim()}
              startIcon={submitting ? <CircularProgress size={18} /> : <RejectIcon />}
            >
              رد مقاله
            </Button>
          </>
        }
      >
        {selectedArticle && (
          <Box sx={{ mb: 2 }}>
            <Typography color="text.secondary" gutterBottom>
              عنوان مقاله:
            </Typography>
            <Typography fontWeight={600}>{selectedArticle.title}</Typography>
          </Box>
        )}
        <TextField
          fullWidth
          label="دلیل رد"
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          multiline
          rows={3}
          required
          sx={{ mt: 1 }}
          placeholder="دلیل رد مقاله را وارد کنید..."
          error={!rejectionReason.trim() && rejectDialogOpen}
          helperText={!rejectionReason.trim() ? 'وارد کردن دلیل رد الزامی است' : ''}
        />
      </ResponsiveModal>

      {/* ==================== REVISION DIALOG ==================== */}
      <ResponsiveModal
        open={revisionDialogOpen}
        onClose={() => setRevisionDialogOpen(false)}
        title="درخواست بازنگری"
        maxWidth="sm"
        disableClose={submitting}
        icon={<RevisionIcon color="warning" />}
        actions={
          <>
            <Button onClick={() => setRevisionDialogOpen(false)} color="inherit">انصراف</Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleRevisionConfirm}
              disabled={submitting || !revisionNote.trim()}
              startIcon={submitting ? <CircularProgress size={18} /> : <RevisionIcon />}
            >
              ارسال درخواست بازنگری
            </Button>
          </>
        }
      >
        {selectedArticle && (
          <Box sx={{ mb: 2 }}>
            <Typography color="text.secondary" gutterBottom>
              عنوان مقاله:
            </Typography>
            <Typography fontWeight={600}>{selectedArticle.title}</Typography>
          </Box>
        )}
        <TextField
          fullWidth
          label="یادداشت بازنگری"
          value={revisionNote}
          onChange={(e) => setRevisionNote(e.target.value)}
          multiline
          rows={4}
          required
          sx={{ mt: 1 }}
          placeholder="نکاتی که نیاز به اصلاح دارند را بنویسید..."
          error={!revisionNote.trim() && revisionDialogOpen}
          helperText={!revisionNote.trim() ? 'وارد کردن یادداشت بازنگری الزامی است' : ''}
        />
      </ResponsiveModal>

      {/* ==================== DELETE DIALOG ==================== */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="حذف مقاله"
        message={
          <Box>
            <Typography>
              آیا از حذف مقاله «{selectedArticle?.title}» اطمینان دارید؟
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              این عملیات قابل بازگشت نیست.
            </Alert>
          </Box>
        }
        confirmText="حذف"
        cancelText="انصراف"
        confirmColor="error"
        loading={submitting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Container>
  );
};

export default ArticleManagement;
